import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

const FAQS = [
  {
    id: 'who-is-this-for',
    q: 'Who is this site for?',
    a: 'Anyone in the UK who has experienced \u2014 or is currently experiencing \u2014 sexual violence or domestic abuse, at any point in their life. It is also for the people who love them, and for professionals. Women, men, non-binary and LGBT+ survivors are all welcome here.',
  },
  {
    id: 'is-it-anonymous',
    q: 'Is it really anonymous?',
    a: 'Yes. There is no sign-up, no account, no cookies that track you. The journal and guided journal save only inside your browser \u2014 nothing you write is uploaded to a server. The only thing the site counts is an anonymous "page opened" event so the campaign knows how many people it has reached.',
  },
  {
    id: 'do-i-have-to-report',
    q: 'Do I have to report what happened to use this site?',
    a: 'No. Nothing on this site requires you to have reported, to be ready to report, or to ever report. Specialist UK support \u2014 including Rape Crisis, Refuge, Women\u2019s Aid, Live Fear Free, ISVA and IDVA services \u2014 are all available whether you have reported or not.',
  },
  {
    id: 'quick-exit',
    q: 'What does the "Exit Now" button do?',
    a: 'It immediately replaces this site in your browser with bbc.co.uk/weather. It is on every page (top-right of the header, and as a floating button bottom-left). If someone walks in unexpectedly, tap it. The back button will not return to The Full Impact.',
  },
  {
    id: 'ground-me',
    q: 'What is the "Ground me" button?',
    a: 'A calm popup with four short grounding techniques (the 5-4-3-2-1 senses exercise, box-breathing with a live animation, orienting to the room, and a cold-water reset). Open it any time you feel panicked, dissociated, or stuck in a flashback. You can stop at any moment.',
  },
  {
    id: 'guided-journal',
    q: 'What is the guided journal?',
    a: 'A calm, step-by-step trauma-informed reflection. It walks you through eight gentle prompts \u2014 grounding, naming feelings, releasing self-blame, finding one small kindness for yourself today. You can skip anything, come back to it any time, and save the result to your private journal. It is also downloadable as a PDF workbook so you can do it on paper.',
  },
  {
    id: 'lost-data',
    q: 'I cleared my browser \u2014 are my journal entries gone?',
    a: 'Because the journal is anonymous and stored only in your browser, clearing browser data (or using private/incognito mode) will erase entries. To keep them long-term, use the "Download backup" button on the journal page to save a .json file, and "Restore from file" to bring them back later.',
  },
  {
    id: 'who-built',
    q: 'Who built The Full Impact?',
    a: 'Angela Moreton \u2014 a UK survivor. Every word, prompt and resource on this site has been written by her or carefully sourced from Rape Crisis, the CPS, NHS, gov.uk and other UK specialist organisations.',
  },
  {
    id: 'copyright',
    q: 'Can I share content from this site?',
    a: 'Most of the website is shared under Creative Commons BY-NC-ND 4.0 \u2014 you may share it with attribution, but not commercially and not in modified form. The guided journal workbook specifically is "All Rights Reserved" because it is original creative writing. Personal use only; please ask before using it in training, publications, or anywhere paid (Instagram @thefullimpactcampaign).',
  },
  {
    id: 'how-to-help',
    q: 'How can I support the campaign?',
    a: 'Follow @thefullimpactcampaign on Instagram, and share this site with anyone who might need it. Word of mouth is the most important thing.',
  },
  {
    id: 'in-crisis',
    q: 'I am in crisis right now \u2014 what do I do?',
    a: 'If you are in immediate danger, call 999. If you cannot speak, dial 999 and press 55 (Silent Solution). If you are struggling with your mental health or thoughts of self-harm, Samaritans answer 24/7 on 116 123. The full list of UK helplines is on the Help Now page.',
  },
];

export default function FAQ() {
  const [openIds, setOpenIds] = useState(new Set());
  const toggle = (id) =>
    setOpenIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="faq-screen">
      <SEO
        title="FAQ \u2014 Common Questions About The Full Impact"
        description="Answers to common questions about The Full Impact: anonymity, the guided journal, quick exit, grounding tools, UK helplines, and copyright."
        path="/app/faq"
      />

      <p className="eyebrow">FREQUENTLY ASKED</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Common questions.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Quick answers about how the site works, your privacy, where the information comes from,
        and what to do if you need help right now.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {FAQS.map((f) => {
          const open = openIds.has(f.id);
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => toggle(f.id)}
              data-testid={`faq-${f.id}`}
              className="tf-card text-left p-5"
              style={{ cursor: 'pointer' }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--tf-purple-light)', color: 'var(--tf-purple-dark)' }}
                >
                  <HelpCircle size={18} />
                </div>
                <h3
                  className="font-heading flex-1"
                  style={{ fontSize: 16.5, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
                >
                  {f.q}
                </h3>
                {open ? (
                  <ChevronUp size={18} color="var(--tf-text-muted)" />
                ) : (
                  <ChevronDown size={18} color="var(--tf-text-muted)" />
                )}
              </div>
              <div
                className="mt-4 pt-4 tf-prose print-show"
                data-print-expand="true"
                style={{
                  borderTop: '1px solid var(--tf-card-border)',
                  display: open ? 'block' : 'none',
                }}
              >
                <p style={{ fontSize: 14, lineHeight: 1.7 }}>{f.a}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
